import { addDays, format } from 'date-fns';
import { dateToSimpleDate, simpleDateToDate } from './dates';
import type { SimpleDate } from './types';

/*
  The day-results route: one page showing every game's result for a single date, served by both
  clients at the same path.

  Today needs no parameter, which keeps the URL people see most often clean — and means a link shared
  from today's page reads as "today" to whoever opens it, rather than pinning them to the sharer's day.
*/

/** What a finished puzzle is worth reporting — the numbers the solved screen and day results show. */
export type PlayResult = {
  durationSec: number;
  hintsUsed: number;
  checksUsed: number;
};

/** The reportable numbers from a play, once it's complete. */
export function playToResult(
  play: {
    complete: boolean;
    play_duration?: number | null;
    play_data?: { playStats?: { hintsUsed?: number; checksUsed?: number } } | null;
  } | null
): PlayResult | undefined {
  if (!play?.complete) return undefined;
  const stats = play.play_data?.playStats;
  return {
    durationSec: play.play_duration ?? 0,
    hintsUsed: stats?.hintsUsed ?? 0,
    checksUsed: stats?.checksUsed ?? 0,
  };
}

export const dayResultsURL = (date: SimpleDate, today: SimpleDate) =>
  date === today ? '/games/results' : `/games/results?date=${date}`;

/**
 * The requested day, or today when the `date` parameter is absent, malformed, or in the future.
 *
 * A bad date in a URL someone typed or shared is not worth an error page; showing today is a sane
 * answer to it. Future dates are folded in too — there are no boards past today, so a page of empty
 * rows would only look broken.
 */
export function parseDayResultsDate(raw: string | null | undefined, today: SimpleDate): SimpleDate {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return today;

  // Catches the well-formed-but-unreal, e.g. 2026-02-31, which Date rolls forward to March 3 rather
  // than rejecting. Round-tripping it is what tells the two apart.
  const parsed = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(parsed.getTime()) || dateToSimpleDate(parsed) !== raw) return today;

  return raw > today ? today : (raw as SimpleDate);
}

/** The day `delta` days from `date`. Negative goes back. */
export const shiftDay = (date: SimpleDate, delta: number): SimpleDate =>
  dateToSimpleDate(addDays(simpleDateToDate(date), delta));

/**
 * The date as the page's header says it: a weekday over a day, e.g. WEDNESDAY / "August 28".
 *
 * The year appears only when it isn't the current one. On today's page — which is most of the traffic
 * — a year reads as clutter, but an archive date from a previous year is genuinely ambiguous without
 * it.
 */
export function formatDayResultsHeading(date: SimpleDate, today: SimpleDate): { weekday: string; day: string } {
  const parsed = simpleDateToDate(date);
  const sameYear = date.slice(0, 4) === today.slice(0, 4);
  return {
    weekday: format(parsed, 'EEEE').toUpperCase(),
    day: format(parsed, sameYear ? 'MMMM d' : 'MMMM d, yyyy'),
  };
}
